import { useEffect, useRef, useState } from 'react';
import { Wave } from '../types';

interface AudioNodes {
    osc: OscillatorNode;
    gain: GainNode;
    panner: StereoPannerNode;
    lfo?: OscillatorNode;
    lfoGain?: GainNode;
}

const getOscillatorType = (waveform?: string): OscillatorType => {
    if (waveform === 'square' || waveform === 'triangle' || waveform === 'sawtooth') return waveform;
    return 'sine';
};

export const useAudio = (waves: Wave[], speed: number = 1) => {
    const [isPlaying, setIsPlaying] = useState(false);
    const audioCtxRef = useRef<AudioContext | null>(null);
    const oscillatorsRef = useRef<Map<number, AudioNodes>>(new Map());
    const masterGainRef = useRef<GainNode | null>(null);

    const toggleAudio = async () => {
        if (!isPlaying) {
            // Init Audio Context on user gesture
            if (!audioCtxRef.current) {
                const AudioContext = window.AudioContext || (window as any).webkitAudioContext;
                audioCtxRef.current = new AudioContext();
                
                // Master Gain (Volume limit)
                const master = audioCtxRef.current.createGain();
                master.gain.value = 0.2; // Reduced volume to prevent clipping with multiple waves
                master.connect(audioCtxRef.current.destination);
                masterGainRef.current = master;
            }

            if (audioCtxRef.current.state === 'suspended') {
                await audioCtxRef.current.resume();
            }
            setIsPlaying(true);
        } else {
            if (audioCtxRef.current) {
                audioCtxRef.current.suspend();
            }
            setIsPlaying(false);
        }
    };

    // Sync React State with Web Audio API
    useEffect(() => {
        if (!audioCtxRef.current || !masterGainRef.current) return;
        const ctx = audioCtxRef.current;
        const now = ctx.currentTime;

        // 1. Remove oscillators for waves that no longer exist or are strictly muted
        const currentIds = new Set(waves.map(w => w.id));
        oscillatorsRef.current.forEach((nodes, id) => {
            if (!currentIds.has(id)) {
                try {
                    nodes.osc.stop();
                    nodes.osc.disconnect();
                    nodes.gain.disconnect();
                    nodes.panner.disconnect();
                    if (nodes.lfo) {
                        nodes.lfo.stop();
                        nodes.lfo.disconnect();
                    }
                    if (nodes.lfoGain) {
                        nodes.lfoGain.disconnect();
                    }
                } catch (e) {
                    // Ignore already stopped errors
                }
                oscillatorsRef.current.delete(id);
            }
        });

        // 2. Create or Update oscillators
        waves.forEach(wave => {
            let nodes = oscillatorsRef.current.get(wave.id);

            if (!nodes) {
                // Create new
                const osc = ctx.createOscillator();
                const gain = ctx.createGain();
                const panner = ctx.createStereoPanner();
                
                osc.type = getOscillatorType(wave.waveform);
                osc.connect(gain);
                gain.connect(panner);
                panner.connect(masterGainRef.current!);
                osc.start();

                nodes = { osc, gain, panner };
                oscillatorsRef.current.set(wave.id, nodes);
            }

            // Update Parameters
            // Apply speed as time dilation to frequency
            const effectiveFreq = wave.freq * speed;
            nodes.osc.frequency.setTargetAtTime(effectiveFreq, now, 0.05);

            // Update waveform shape
            const oscType = getOscillatorType(wave.waveform);
            if (nodes.osc.type !== oscType) {
                nodes.osc.type = oscType;
            }

            // Amplitude (Volume)
            const targetGain = wave.muted ? 0 : (wave.amp / 100);
            nodes.gain.gain.setTargetAtTime(targetGain, now, 0.05);

            // Auto-Pan LFO (for EMDR bilateral audio)
            const autoPanRate = wave.autoPanRate || 0;
            const autoPanDepth = (wave.autoPanDepth ?? 0) / 100; // Convert 0-100 to 0-1

            if (autoPanRate > 0 && autoPanDepth > 0) {
                // Create LFO if it doesn't exist
                if (!nodes.lfo || !nodes.lfoGain) {
                    const lfo = ctx.createOscillator();
                    const lfoGain = ctx.createGain();
                    
                    lfo.type = 'sine';
                    lfo.connect(lfoGain);
                    lfoGain.connect(nodes.panner.pan);
                    lfo.start();
                    
                    nodes.lfo = lfo;
                    nodes.lfoGain = lfoGain;
                    oscillatorsRef.current.set(wave.id, nodes);
                }
                
                // Update LFO parameters
                nodes.lfo.frequency.setTargetAtTime(autoPanRate, now, 0.05);
                nodes.lfoGain.gain.setTargetAtTime(autoPanDepth, now, 0.05);
                
                // Set base pan to 0 (center) when using auto-pan
                nodes.panner.pan.setTargetAtTime(0, now, 0.05);
            } else {
                // Disconnect LFO if auto-pan is disabled
                if (nodes.lfo && nodes.lfoGain) {
                    try {
                        nodes.lfo.stop();
                        nodes.lfo.disconnect();
                        nodes.lfoGain.disconnect();
                    } catch (e) {
                        // Ignore errors
                    }
                    nodes.lfo = undefined;
                    nodes.lfoGain = undefined;
                }
                
                // Static channel panning (-1 = left, 0 = center/both, 1 = right)
                const channel = wave.channel || 'both';
                const panValue = channel === 'left' ? -1 : channel === 'right' ? 1 : 0;
                nodes.panner.pan.setTargetAtTime(panValue, now, 0.05);
            }
        });

    }, [waves, isPlaying, speed]);

    // Cleanup on unmount: stop all audio and release the context
    useEffect(() => {
        return () => {
            try {
                // Stop and disconnect all oscillators and LFOs
                oscillatorsRef.current.forEach(nodes => {
                    try {
                        nodes.osc.stop();
                        nodes.osc.disconnect();
                        nodes.gain.disconnect();
                        nodes.panner.disconnect();
                        if (nodes.lfo) {
                            nodes.lfo.stop();
                            nodes.lfo.disconnect();
                        }
                        if (nodes.lfoGain) {
                            nodes.lfoGain.disconnect();
                        }
                    } catch {
                        // ignore
                    }
                });
                oscillatorsRef.current.clear();

                // Disconnect master gain
                if (masterGainRef.current) {
                    try { masterGainRef.current.disconnect(); } catch {}
                    masterGainRef.current = null;
                }

                // Close audio context to ensure audio stops
                if (audioCtxRef.current) {
                    try { audioCtxRef.current.close(); } catch {}
                    audioCtxRef.current = null;
                }
            } catch {
                // ignore cleanup errors
            }
        };
    }, []);

    return { isPlaying, toggleAudio };
};
